import { type FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MdArrowForward, 
  MdArrowBack, 
  MdCheck,
  MdViewKanban,
  MdCalendarToday,
  MdChat,
  MdPalette,
  MdPhone,
  MdComputer
} from 'react-icons/md';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';

interface WelcomeStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  mobileImage?: string;
  desktopImage?: string;
  features?: string[];
}

const Welcome: FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { profile, updateProfile } = useProfile(user?.id);
  const [currentStep, setCurrentStep] = useState(0);
  const [showMobile, setShowMobile] = useState(true);

  const steps: WelcomeStep[] = [
    {
      id: 0,
      title: '👋 Welcome to Taskaban!',
      description: 'Your AI-powered task management companion. Let\'s take a quick tour to help you get started.',
      icon: <MdCheck className="w-16 h-16" />,
      features: [
        'Drag-and-drop Kanban boards',
        'AI chatbot assistant',
        'Multiple calendar views',
        '9 beautiful themes',
        'Real-time sync across devices'
      ]
    },
    {
      id: 1,
      title: 'Kanban Board Magic',
      description: 'Organize your tasks visually with our intuitive drag-and-drop Kanban board. Move tasks between Pending, In Progress, and Completed with ease.',
      icon: <MdViewKanban className="w-16 h-16" />,
      mobileImage: '/image samples/kanban-mobile.jpeg',
      desktopImage: '/image samples/kanban-showcase.png',
      features: [
        'Drag tasks between columns',
        'Color-coded priorities',
        'Quick task editing',
        'Touch-optimized for mobile'
      ]
    },
    {
      id: 2,
      title: 'AI Chatbot Assistant',
      description: 'Create and manage tasks using natural language. Just chat with our AI assistant and let it do the heavy lifting!',
      icon: <MdChat className="w-16 h-16" />,
      desktopImage: '/image samples/ai-chat-showcase.png',
      features: [
        '"Create a task to buy groceries tomorrow"',
        '"Show me all high priority tasks"',
        '"Mark the meeting task as completed"',
        'Understands dates and priorities'
      ]
    },
    {
      id: 3,
      title: 'Calendar & Timeline Views',
      description: 'Visualize your tasks in calendar and timeline formats. See your productivity patterns with the heat map view.',
      icon: <MdCalendarToday className="w-16 h-16" />,
      mobileImage: '/image samples/mobile-calendar.jpeg',
      desktopImage: '/image samples/calendar-view.png',
      features: [
        'Monthly calendar view',
        'Weekly timeline',
        'Productivity heat map',
        'Quick task spotlight'
      ]
    },
    {
      id: 4,
      title: 'Beautiful Themes',
      description: 'Personalize your experience with 9 stunning color themes. Each theme comes with both light and dark mode.',
      icon: <MdPalette className="w-16 h-16" />,
      mobileImage: '/image samples/mobile-green-dark.jpeg',
      desktopImage: '/image samples/theme-showcase.png',
      features: [
        'Purple, Blue, Green & more',
        'Light and Dark modes',
        'Smooth theme transitions',
        'Save your preference'
      ]
    }
  ];

  // Mark welcome as completed when user finishes
  useEffect(() => {
    // If profile is loaded and has_seen_welcome is true, redirect immediately
    if (profile?.has_seen_welcome === true && !window.location.search.includes('force')) {
      navigate('/dashboard', { replace: true });
    }
  }, [profile, navigate]);

  const handleComplete = async () => {
    try {
      // Set session storage to prevent redirect loop
      sessionStorage.setItem('taskaban_welcome_shown_this_session', 'true');
      
      // Update profile in database
      await updateProfile({ has_seen_welcome: true });
      
      // Also store in localStorage as backup
      localStorage.setItem('taskaban_welcome_completed', 'true');
      
      // Use replace to prevent back button from returning to welcome
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to update profile:', error);
      // Fallback to localStorage only
      localStorage.setItem('taskaban_welcome_completed', 'true');
      sessionStorage.setItem('taskaban_welcome_shown_this_session', 'true');
      navigate('/dashboard', { replace: true });
    }
  };

  const handleSkip = async () => {
    try {
      // Set session storage to prevent redirect loop
      sessionStorage.setItem('taskaban_welcome_shown_this_session', 'true');
      
      // Update profile in database
      await updateProfile({ has_seen_welcome: true });
      
      // Also store in localStorage as backup
      localStorage.setItem('taskaban_welcome_completed', 'true');
      
      // Use replace to prevent back button from returning to welcome
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to update profile:', error);
      // Fallback to localStorage only
      localStorage.setItem('taskaban_welcome_completed', 'true');
      sessionStorage.setItem('taskaban_welcome_shown_this_session', 'true');
      navigate('/dashboard', { replace: true });
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-3 sm:p-4 relative overflow-hidden"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(135deg, rgba(17, 24, 39, 1) 0%, rgba(139, 0, 255, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(240, 244, 248, 1) 0%, rgba(139, 0, 255, 0.1) 100%)'
      }}
    >
      {/* Skip Button - Always visible */}
      <button
        onClick={handleSkip}
        className="fixed top-3 right-3 sm:top-4 sm:right-4 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-sm font-semibold transition-all z-20 hover:shadow-md"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(55, 65, 81, 0.8)' : 'rgba(229, 231, 235, 0.8)',
          color: theme === 'dark' ? '#e5e7eb' : '#374151',
          backdropFilter: 'blur(10px)'
        }}
      >
        Skip
      </button>

      <div className="max-w-6xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl shadow-2xl border-2 overflow-hidden"
          style={{
            backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
            borderColor: 'var(--color-theme)'
          }}
        >
          {/* Progress Bar */}
          <div className="h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700">
            <motion.div
              className="h-full"
              style={{ backgroundColor: 'var(--color-theme)' }}
              initial={{ width: '0%' }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          <div className="p-4 sm:p-6 md:p-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Icon */}
                <div 
                  className="flex justify-center mb-4 sm:mb-6"
                  style={{ color: 'var(--color-theme)' }}
                >
                  {step.icon}
                </div>

                {/* Title */}
                <h1 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4 px-2"
                  style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
                >
                  {step.title}
                </h1>

                {/* Description */}
                <p 
                  className="text-base sm:text-lg text-center mb-6 sm:mb-8 max-w-2xl mx-auto px-2"
                  style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                >
                  {step.description}
                </p>

                {/* Image Preview Section */}
                {(step.mobileImage || step.desktopImage) && (
                  <div className="mb-6 sm:mb-8">
                    {/* Device Toggle */}
                    {step.mobileImage && step.desktopImage && (
                      <div className="flex justify-center gap-2 mb-4">
                        <button
                          onClick={() => setShowMobile(false)}
                          className={`
                            flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm transition-all
                            ${!showMobile ? 'shadow-lg' : ''}
                          `}
                          style={{
                            backgroundColor: !showMobile 
                              ? 'var(--color-theme)' 
                              : (theme === 'dark' ? '#374151' : '#e5e7eb'),
                            color: !showMobile 
                              ? '#ffffff' 
                              : (theme === 'dark' ? '#9ca3af' : '#6b7280')
                          }}
                        >
                          <MdComputer className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="hidden xs:inline">Desktop</span>
                        </button>
                        <button
                          onClick={() => setShowMobile(true)}
                          className={`
                            flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm transition-all
                            ${showMobile ? 'shadow-lg' : ''}
                          `}
                          style={{
                            backgroundColor: showMobile 
                              ? 'var(--color-theme)' 
                              : (theme === 'dark' ? '#374151' : '#e5e7eb'),
                            color: showMobile 
                              ? '#ffffff' 
                              : (theme === 'dark' ? '#9ca3af' : '#6b7280')
                          }}
                        >
                          <MdPhone className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="hidden xs:inline">Mobile</span>
                        </button>
                      </div>
                    )}

                    {/* Image Display */}
                    <div className="flex justify-center px-2">
                      <motion.img
                        key={showMobile ? step.mobileImage : step.desktopImage}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        src={showMobile && step.mobileImage ? step.mobileImage : step.desktopImage}
                        alt={step.title}
                        className="rounded-lg shadow-xl border-2 max-h-[300px] sm:max-h-[400px] w-auto object-contain"
                        style={{ borderColor: 'var(--color-theme)' }}
                      />
                    </div>
                  </div>
                )}

                {/* Features List */}
                {step.features && (
                  <div className="max-w-2xl mx-auto px-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {step.features.map((feature, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg"
                          style={{
                            backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb'
                          }}
                        >
                          <MdCheck 
                            className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5"
                            style={{ color: 'var(--color-theme)' }}
                          />
                          <span 
                            className="text-xs sm:text-sm leading-relaxed"
                            style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}
                          >
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation - Mobile Optimized */}
            <div className="flex flex-col sm:flex-row items-center justify-between mt-8 sm:mt-10 pt-6 border-t gap-4" style={{ borderColor: theme === 'dark' ? '#374151' : '#e5e7eb' }}>
              {/* Back Button */}
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className="flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-md w-full sm:w-auto order-2 sm:order-1"
                style={{
                  backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                  color: theme === 'dark' ? '#e5e7eb' : '#374151'
                }}
              >
                <MdArrowBack className="w-5 h-5" />
                <span>Back</span>
              </button>

              {/* Step Indicators */}
              <div className="flex items-center gap-2 order-1 sm:order-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className="w-2 h-2 rounded-full transition-all"
                    aria-label={`Go to step ${index + 1}`}
                    style={{
                      backgroundColor: index === currentStep 
                        ? 'var(--color-theme)' 
                        : (theme === 'dark' ? '#374151' : '#d1d5db'),
                      transform: index === currentStep ? 'scale(1.5)' : 'scale(1)'
                    }}
                  />
                ))}
              </div>

              {/* Next / Get Started Button */}
              <button
                onClick={nextStep}
                className="flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-lg w-full sm:w-auto order-3"
                style={{
                  backgroundColor: 'var(--color-theme)',
                  color: '#ffffff'
                }}
              >
                <span>{isLastStep ? 'Get Started' : 'Next'}</span>
                <MdArrowForward className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Welcome Message */}
        {user && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-4 sm:mt-6 text-sm px-4"
            style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
          >
            Welcome, {user.email?.split('@')[0]}! 👋
          </motion.p>
        )}
      </div>
    </div>
  );
};

export default Welcome;
